import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './styles/ConnectMessages.css';

export default function ConnectMessages() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMobileNav, setShowMobileNav] = useState(false);

  // Available users to start conversations with
  const [availableUsers] = useState([
    { id: 4, name: 'Maya Thompson', pronouns: 'she/they', location: 'Miami, USA', online: false },
    { id: 5, name: 'Chris Martinez', pronouns: 'he/him', location: 'Willemstad, Curaçao', online: true },
    { id: 6, name: 'Taylor Brooks', pronouns: 'they/them', location: 'London, UK', online: false },
    { id: 7, name: 'Jamie Lee', pronouns: 'she/her', location: 'Kingston, Jamaica', online: true },
    { id: 8, name: 'Morgan Davis', pronouns: 'he/they', location: 'New York, USA', online: false }
  ]);

  // Mock conversations data
  const [conversations] = useState([
    {
      id: 1,
      user: {
        name: 'Alex Rivera',
        avatar: null,
        pronouns: 'they/them',
        location: 'San Juan, Puerto Rico',
        online: true
      },
      lastMessage: 'Hey! Are you still planning to visit Curaçao next month?',
      timestamp: Date.now() - 1000 * 60 * 5, // 5 mins ago
      unread: 2
    },
    {
      id: 2,
      user: {
        name: 'Jordan Chen',
        avatar: null,
        pronouns: 'she/her',
        location: 'Toronto, Canada',
        online: false
      },
      lastMessage: 'The beach resort was amazing! Thanks for the recommendation 🌴',
      timestamp: Date.now() - 1000 * 60 * 60 * 2, // 2 hours ago
      unread: 0
    },
    {
      id: 3,
      user: {
        name: 'Sam Williams',
        avatar: null,
        pronouns: 'he/him',
        location: 'Bridgetown, Barbados',
        online: true
      },
      lastMessage: 'Let me know when you arrive, I can show you around!',
      timestamp: Date.now() - 1000 * 60 * 60 * 24, // 1 day ago
      unread: 0
    }
  ]);

  // Mock messages for selected conversation
  const [messages] = useState({
    1: [
      { id: 1, sender: 'them', text: 'Hi there! I saw you\'re planning a trip to the Caribbean', timestamp: Date.now() - 1000 * 60 * 60 },
      { id: 2, sender: 'me', text: 'Yes! I\'m so excited. Have you been to Curaçao?', timestamp: Date.now() - 1000 * 60 * 30 },
      { id: 3, sender: 'them', text: 'I have! It\'s one of my favorite islands. Very LGBTQ+ friendly', timestamp: Date.now() - 1000 * 60 * 15 },
      { id: 4, sender: 'them', text: 'Hey! Are you still planning to visit Curaçao next month?', timestamp: Date.now() - 1000 * 60 * 5 }
    ],
    2: [
      { id: 1, sender: 'them', text: 'Just got back from Aruba!', timestamp: Date.now() - 1000 * 60 * 60 * 3 },
      { id: 2, sender: 'me', text: 'How was it? Did you check out that resort I mentioned?', timestamp: Date.now() - 1000 * 60 * 60 * 2.5 },
      { id: 3, sender: 'them', text: 'The beach resort was amazing! Thanks for the recommendation 🌴', timestamp: Date.now() - 1000 * 60 * 60 * 2 }
    ],
    3: [
      { id: 1, sender: 'me', text: 'Hey Sam! I\'ll be in Barbados next week', timestamp: Date.now() - 1000 * 60 * 60 * 25 },
      { id: 2, sender: 'them', text: 'That\'s awesome! How long will you be staying?', timestamp: Date.now() - 1000 * 60 * 60 * 24.5 },
      { id: 3, sender: 'me', text: 'About 5 days. Any must-see spots?', timestamp: Date.now() - 1000 * 60 * 60 * 24.2 },
      { id: 4, sender: 'them', text: 'Let me know when you arrive, I can show you around!', timestamp: Date.now() - 1000 * 60 * 60 * 24 }
    ]
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('connectUser');
    if (!storedUser) {
      navigate('/connect');
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('connectUser');
    navigate('/connect');
  };

  const formatTime = (timestamp) => {
    const diff = Date.now() - timestamp;
    if (diff < 1000 * 60 * 60) {
      return `${Math.floor(diff / (1000 * 60))}m ago`;
    } else if (diff < 1000 * 60 * 60 * 24) {
      return `${Math.floor(diff / (1000 * 60 * 60))}h ago`;
    } else {
      return `${Math.floor(diff / (1000 * 60 * 60 * 24))}d ago`;
    }
  };

  const handleSendMessage = () => {
    if (messageInput.trim()) {
      // In a real app, this would send to backend
      console.log('Sending message:', messageInput);
      setMessageInput('');
    }
  };

  const handleStartNewChat = (selectedUser) => {
    // In a real app, this would create a new conversation in the backend
    console.log('Starting new chat with:', selectedUser.name);
    setShowNewChatModal(false);
    setSearchQuery('');
    // For demo purposes, just show a message
    alert(`New chat started with ${selectedUser.name}! (This is a demo - in production, this would create a real conversation)`);
  };

  const filteredUsers = availableUsers.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!user) return null;

  return (
    <div className="connect-messages-page">
      {/* Header */}
      <header className="connect-profile-header">
        <Link to="/" className="connect-profile-logo">
          <img src={process.env.PUBLIC_URL + '/assets/logo.png'} alt="OutCaribbean" />
        </Link>
        <nav className="connect-profile-nav">
          <Link to="/connect/profile" className="connect-nav-link">Profile</Link>
          <Link to="/connect/discover" className="connect-nav-link">Discover</Link>
          <Link to="/connect/messages" className="connect-nav-link active">Messages</Link>
        </nav>
        <button className="connect-logout-btn" onClick={handleLogout}>
          Log Out
        </button>
        <button className="connect-mobile-nav-toggle" onClick={() => setShowMobileNav(true)}>
          <span></span>
          <span></span>
          <span></span>
        </button>
      </header>

      {/* Mobile Navigation */}
      {showMobileNav && (
        <>
          <div className="connect-mobile-nav-overlay active" onClick={() => setShowMobileNav(false)}></div>
          <div className="connect-mobile-nav-menu active">
            <div className="connect-mobile-nav-header">
              <h3>Menu</h3>
              <button className="connect-mobile-nav-close" onClick={() => setShowMobileNav(false)}>×</button>
            </div>
            <div className="connect-mobile-nav-links">
              <Link to="/connect/profile" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Profile</Link>
              <Link to="/connect/discover" className="connect-nav-link" onClick={() => setShowMobileNav(false)}>Discover</Link>
              <Link to="/connect/messages" className="connect-nav-link active" onClick={() => setShowMobileNav(false)}>Messages</Link>
            </div>
            <div className="connect-mobile-nav-footer">
              <button className="connect-logout-btn" onClick={handleLogout}>Log Out</button>
            </div>
          </div>
        </>
      )}

      {/* Messages Container */}
      <div className="connect-messages-container">
        {/* Conversations List */}
        <div className="connect-conversations-panel">
          <div className="connect-conversations-header">
            <h2>Messages</h2>
            <button 
              className="connect-new-message-btn" 
              title="New Message"
              onClick={() => setShowNewChatModal(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <line x1="12" y1="8" x2="12" y2="14"/>
                <line x1="9" y1="11" x2="15" y2="11"/>
              </svg>
            </button>
          </div>

          <div className="connect-conversations-search">
            <input type="text" placeholder="Search messages..." />
          </div>

          <div className="connect-conversations-list">
            {conversations.map(conv => (
              <div
                key={conv.id}
                className={`connect-conversation-item ${selectedConversation?.id === conv.id ? 'active' : ''} ${conv.unread > 0 ? 'unread' : ''}`}
                onClick={() => setSelectedConversation(conv)}
              >
                <div className="connect-conv-avatar">
                  {conv.user.avatar ? (
                    <img src={conv.user.avatar} alt={conv.user.name} />
                  ) : (
                    <div className="connect-conv-avatar-placeholder">
                      {conv.user.name.charAt(0)}
                    </div>
                  )}
                  {conv.user.online && <span className="connect-online-dot"></span>}
                </div>
                <div className="connect-conv-info">
                  <div className="connect-conv-header">
                    <span className="connect-conv-name">{conv.user.name}</span>
                    <span className="connect-conv-time">{formatTime(conv.timestamp)}</span>
                  </div>
                  <p className="connect-conv-preview">{conv.lastMessage}</p>
                </div>
                {conv.unread > 0 && (
                  <span className="connect-unread-badge">{conv.unread}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Chat Panel */}
        <div className={`connect-chat-panel ${selectedConversation ? 'active' : ''}`}>
          {selectedConversation ? (
            <>
              {/* Chat Header */}
              <div className="connect-chat-header">
                <button className="connect-chat-back-btn" onClick={() => setSelectedConversation(null)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="15 18 9 12 15 6"/>
                  </svg>
                </button>
                <div className="connect-chat-user-info">
                  <div className="connect-chat-avatar">
                    {selectedConversation.user.avatar ? (
                      <img src={selectedConversation.user.avatar} alt={selectedConversation.user.name} />
                    ) : (
                      <div className="connect-chat-avatar-placeholder">
                        {selectedConversation.user.name.charAt(0)}
                      </div>
                    )}
                  </div>
                  <div>
                    <h3>{selectedConversation.user.name}</h3>
                    <span className="connect-chat-status">
                      {selectedConversation.user.online ? '🟢 Online' : '⚫ Offline'} · {selectedConversation.user.location}
                    </span>
                  </div>
                </div>
                <div className="connect-chat-actions">
                  <button title="View Profile">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </button>
                  <button title="More Options">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="1"/>
                      <circle cx="19" cy="12" r="1"/>
                      <circle cx="5" cy="12" r="1"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="connect-chat-messages">
                {messages[selectedConversation.id]?.map(msg => (
                  <div key={msg.id} className={`connect-message ${msg.sender === 'me' ? 'sent' : 'received'}`}>
                    <div className="connect-message-bubble">
                      <p>{msg.text}</p>
                      <span className="connect-message-time">{formatTime(msg.timestamp)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="connect-chat-input">
                <button className="connect-attach-btn" title="Attach">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <input
                  type="text"
                  placeholder="Type a message..."
                  value={messageInput}
                  onChange={(e) => setMessageInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                />
                <button className="connect-send-btn" onClick={handleSendMessage} disabled={!messageInput.trim()}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                </button>
              </div>
            </>
          ) : (
            <div className="connect-chat-empty">
              <div className="connect-chat-empty-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <h3>Your Messages</h3>
              <p>Select a conversation to start chatting</p>
              <Link to="/connect/discover" className="connect-find-people-btn">
                Find People to Connect
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="connect-new-chat-overlay" onClick={() => setShowNewChatModal(false)}>
          <div className="connect-new-chat-modal" onClick={(e) => e.stopPropagation()}>
            <div className="connect-new-chat-header">
              <h3>New Message</h3>
              <button className="connect-new-chat-close" onClick={() => setShowNewChatModal(false)}>
                ×
              </button>
            </div>

            <div className="connect-new-chat-search">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder="Search people..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="connect-new-chat-users">
              {filteredUsers.length > 0 ? (
                filteredUsers.map(person => (
                  <div 
                    key={person.id} 
                    className="connect-new-chat-user"
                    onClick={() => handleStartNewChat(person)}
                  >
                    <div className="connect-new-chat-avatar">
                      <div className="connect-new-chat-avatar-placeholder">
                        {person.name.charAt(0)}
                      </div>
                      {person.online && <span className="connect-online-dot"></span>}
                    </div>
                    <div className="connect-new-chat-info">
                      <span className="connect-new-chat-name">{person.name}</span>
                      <span className="connect-new-chat-details">
                        {person.pronouns} · {person.location}
                      </span>
                    </div>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </div>
                ))
              ) : (
                <div className="connect-new-chat-empty">
                  <p>No users found</p>
                </div>
              )}
            </div>

            <div className="connect-new-chat-footer">
              <Link to="/connect/discover" className="connect-discover-link" onClick={() => setShowNewChatModal(false)}>
                Discover more people →
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
